import type { Ref } from "preact";
import { useState } from "preact/hooks";

import { HIDE_PASSWORD_LABEL, SHOW_PASSWORD_LABEL } from "../lib/authCopy";

export interface PasswordToggleInputProps {
  id: string;
  value: string;
  autoComplete: "current-password" | "new-password";
  onInput: (value: string) => void;
  disabled?: boolean;
  /** TD-058 — lets a sheet give this field initial focus via
   * `useModalA11y`'s `initialFocusRef` (the full-page auth screens focus
   * their heading instead, so they pass nothing). Optional and additive:
   * every existing caller is unchanged. */
  inputRef?: Ref<HTMLInputElement>;
}

/**
 * Sprint-016 F4 (AUTH-01, ui-redesign-direction-2026-07-11.md item 4) —
 * matched eye/eye-slash SVG pair (Feather Icons, MIT-licensed) replacing
 * the mismatched 🙈/👁 emoji pair. `stroke="currentColor"` so it inherits
 * `.auth-toggle-btn`'s existing (unstyled) color, i.e. `var(--ink)` from
 * `body` — no CSS change needed. Sized to `18` to match the glyph's
 * previous `font-size: 18px` footprint inside the 48×48 box.
 */
const EYE_ICON = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EYE_OFF_ICON = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

/**
 * Sprint-007 D4 — the show/hide password field shared by every auth
 * screen in this sprint's scope (`LoginView`, `CredentialSetupView`).
 * Defaults to VISIBLE text (UX §1.2/§2.1: "This is a private, one-time
 * moment... mistype risk from gloves/bright-sun/non-technical users
 * choosing their own password... is the dominant real risk"), toggled by
 * an eye button that is always present and ≥44px (`--tap-min`, via
 * `.auth-toggle-btn`). Deliberately has NO `onPaste` handler anywhere in
 * this component — UX §1.2/§5: paste must never be blocked in a password
 * field, and the only way to accidentally block it is to add one.
 */
export function PasswordToggleInput({
  id,
  value,
  autoComplete,
  onInput,
  disabled = false,
  inputRef,
}: PasswordToggleInputProps) {
  // Sprint-007 D4 — visible-by-default per UX §1.2's table row: "Show/hide
  // toggle default: ON (visible text, not dots)."
  const [visible, setVisible] = useState(true);

  return (
    <div class="auth-password-row">
      <input
        ref={inputRef}
        id={id}
        class="pick-input"
        type={visible ? "text" : "password"}
        autocomplete={autoComplete}
        value={value}
        required
        aria-required="true"
        disabled={disabled}
        onInput={(e) => onInput((e.target as HTMLInputElement).value)}
      />
      <button
        type="button"
        class="auth-toggle-btn"
        aria-pressed={visible}
        aria-label={visible ? HIDE_PASSWORD_LABEL : SHOW_PASSWORD_LABEL}
        title={visible ? "Hide password" : "Show password"}
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? EYE_OFF_ICON : EYE_ICON}
      </button>
    </div>
  );
}
